import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import BookCard from '../components/BookCard';
import SearchBar from '../components/SearchBar';
import { LoaderIcon } from '../components/Icons';
import styles from './Home.module.css';

const SLIDE_INTERVAL_MS = 5000;

const Home = () => {
  const navigate = useNavigate();
  const [carouselItems, setCarouselItems] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categorySections, setCategorySections] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState('');
  const [latestNovels, setLatestNovels] = useState([]);
  const [recommendedNovels, setRecommendedNovels] = useState([]);
  const [highlightsLoading, setHighlightsLoading] = useState(true);
  const [highlightsError, setHighlightsError] = useState('');

  useEffect(() => {
    const fetchCarouselItems = async () => {
      try {
        setLoading(true);
        setError('');
        const { data } = await api.get('/carousel-items/', {
          params: { active_only: true },
        });
        if (Array.isArray(data)) {
          setCarouselItems(data);
          setCurrentSlide(0);
        } else {
          setCarouselItems([]);
          console.warn('Unexpected carousel payload:', data);
        }
      } catch (err) {
        console.error('获取轮播图失败:', err);
        setError('轮播图加载失败，请稍后重试');
        setCarouselItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCarouselItems();
    fetchFeaturedCategories();
    fetchHighlights();
  }, []);

  const fetchFeaturedCategories = async () => {
    try {
      setCategoriesLoading(true);
      setCategoriesError('');
      const { data } = await api.get('/categories/', {
        params: {
          limit: 50,
        },
      });
      if (Array.isArray(data)) {
        const normalized = data
          .map((category) => {
            const sortedNovels = [...(category.novels || [])].sort((a, b) => {
              const orderA =
                typeof a.feature_order === 'number' ? a.feature_order : Number.MAX_SAFE_INTEGER;
              const orderB =
                typeof b.feature_order === 'number' ? b.feature_order : Number.MAX_SAFE_INTEGER;
              if (orderA !== orderB) {
                return orderA - orderB;
              }
              return (a.id || 0) - (b.id || 0);
            });

            const topThree = sortedNovels.slice(0, 3);
            return {
              id: category.id,
              name: category.name,
              feature_order: category.feature_order ?? 0,
              novels: topThree,
            };
          })
          .filter((section) => section.novels && section.novels.length > 0)
          .sort((a, b) => a.feature_order - b.feature_order);

        setCategorySections(normalized);
      } else {
        setCategorySections([]);
      }
    } catch (err) {
      console.error('获取分类数据失败:', err);
      setCategoriesError('分类数据加载失败，请稍后重试');
      setCategorySections([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchHighlights = async () => {
    try {
      setHighlightsLoading(true);
      setHighlightsError('');
      const [latestRes, recommendedRes] = await Promise.all([
        api.get('/novels/latest', {
          params: { limit: 6 },
        }),
        api.get('/novels/recommended', {
          params: { limit: 6 },
        }),
      ]);

      setLatestNovels(Array.isArray(latestRes.data) ? latestRes.data : []);
      setRecommendedNovels(Array.isArray(recommendedRes.data) ? recommendedRes.data : []);
    } catch (err) {
      console.error('获取推荐小说失败:', err);
      setHighlightsError('推荐数据加载失败，请稍后重试');
      setLatestNovels([]);
      setRecommendedNovels([]);
    } finally {
      setHighlightsLoading(false);
    }
  };

  useEffect(() => {
    if (carouselItems.length <= 1) {
      return undefined;
    }

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
    }, SLIDE_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [carouselItems.length]);

  useEffect(() => {
    if (currentSlide >= carouselItems.length) {
      setCurrentSlide(0);
    }
  }, [carouselItems.length, currentSlide]);

  const hasCarousel = useMemo(() => carouselItems.length > 0, [carouselItems.length]);

  const handleIndicatorClick = (index) => {
    setCurrentSlide(index);
  };

  const handleSearchSubmit = (term) => {
    const normalized = typeof term === 'string' ? term.trim() : '';
    if (!normalized) {
      navigate('/discover');
      return;
    }
    navigate(`/discover?search=${encodeURIComponent(normalized)}`);
  };

  const renderSlideContent = (item, index) => {
    const linkUrl =
      typeof item.link_url === 'string' ? item.link_url.trim() : '';
    const SlideComponent = linkUrl ? 'a' : 'div';
    const slideProps = linkUrl
      ? {
          href: linkUrl,
          target: linkUrl.startsWith('http') ? '_blank' : undefined,
          rel: linkUrl.startsWith('http') ? 'noopener noreferrer' : undefined,
        }
      : {};

    return (
      <SlideComponent
        key={item.id || index}
        className={styles.carouselSlide}
        {...slideProps}
      >
        <img
          src={item.image_url}
          alt={item.title || `轮播图 ${index + 1}`}
          className={styles.carouselImage}
          loading={index === 0 ? 'eager' : 'lazy'}
        />
        {(item.title || item.link_url) && (
          <div className={styles.carouselCaption}>
            <span>{item.title || '查看详情'}</span>
          </div>
        )}
      </SlideComponent>
    );
  };

  const handleViewMore = (category) => {
    if (!category) {
      navigate('/discover');
      return;
    }

    const { id, name } = category;
    if (id) {
      navigate(`/categories/${id}`, {
        state: {
          categoryName: name || '',
        },
      });
      return;
    }

    if (name) {
      navigate(`/discover?category=${encodeURIComponent(name)}`);
      return;
    }

    navigate('/discover');
  };

  return (
    <div className={styles.home}>
      <section className={styles.searchSection}>
        <div className={styles.searchBarWrapper}>
          <SearchBar onSearch={handleSearchSubmit} placeholder="搜索书名、作者..." />
        </div>
      </section>

      <section className={styles.carouselSection}>
        {loading && (
          <div className={styles.carouselStatus} role="status">
            正在加载轮播图...
          </div>
        )}

        {!loading && error && (
          <div className={styles.carouselStatus} role="alert">
            {error}
          </div>
        )}

        {!loading && !error && hasCarousel && (
          <div className={styles.carousel}>
            <div
              className={styles.carouselTrack}
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {carouselItems.map((item, index) => renderSlideContent(item, index))}
            </div>

            {carouselItems.length > 1 && (
              <div className={styles.carouselIndicators}>
                {carouselItems.map((_, index) => (
                  <button
                    key={`indicator-${index}`}
                    type="button"
                    className={`${styles.carouselIndicator} ${
                      currentSlide === index ? styles.carouselIndicatorActive : ''
                    }`}
                    onClick={() => handleIndicatorClick(index)}
                    aria-label={`切换到第 ${index + 1} 张`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      <section className={styles.categoryShowcase}>
        {categoriesLoading && (
          <div className={styles.categoryStatus} role="status">
            正在加载分类...
          </div>
        )}

        {!categoriesLoading && categoriesError && (
          <div className={styles.categoryStatus} role="alert">
            {categoriesError}
          </div>
        )}

        {!highlightsLoading && highlightsError && (
          <div className={styles.categoryStatus} role="alert">
            {highlightsError}
          </div>
        )}

        {!highlightsLoading && !highlightsError && (
          <>
            <div className={styles.categorySection}>
              <div className={styles.categoryHeader}>
                <h2 className={styles.categoryTitle}>最近更新</h2>
                <button
                  type="button"
                  className={styles.categoryMore}
                  onClick={() => navigate('/discover')}
                >
                  更多
                </button>
              </div>
              <div className={styles.categoryBooks}>
                {latestNovels.length > 0 ? (
                  latestNovels.map((novel) => (
                    <BookCard
                      key={`latest-${novel.id}`}
                      book={{
                        id: novel.id,
                        title: novel.title,
                        author: novel.author,
                        description: novel.description,
                        category: Array.isArray(novel.categories) && novel.categories[0]?.name,
                        is_completed: novel.is_completed ?? false,
                      }}
                      from="/"
                      showStatus
                    />
                  ))
                ) : (
                  <div className={styles.categoryStatus}>暂无最近更新的作品</div>
                )}
              </div>
            </div>

            <div className={styles.categorySection}>
              <div className={styles.categoryHeader}>
                <h2 className={styles.categoryTitle}>精选推荐</h2>
                <button
                  type="button"
                  className={styles.categoryMore}
                  onClick={() => navigate('/discover')}
                >
                  更多
                </button>
              </div>
              <div className={styles.categoryBooks}>
                {recommendedNovels.length > 0 ? (
                  recommendedNovels.map((novel) => (
                    <BookCard
                      key={`rec-${novel.id}`}
                      book={{
                        id: novel.id,
                        title: novel.title,
                        author: novel.author,
                        description: novel.description,
                        category: Array.isArray(novel.categories) && novel.categories[0]?.name,
                        is_completed: novel.is_completed ?? false,
                      }}
                      from="/"
                      showStatus
                    />
                  ))
                ) : (
                  <div className={styles.categoryStatus}>暂无推荐的作品</div>
                )}
              </div>
            </div>
          </>
        )}

        {!categoriesLoading && !categoriesError && categorySections.map((section) => (
          <div key={section.id} className={styles.categorySection}>
            <div className={styles.categoryHeader}>
              <h2 className={styles.categoryTitle}>{section.name}</h2>
              <button
                type="button"
                className={styles.categoryMore}
                onClick={() => handleViewMore(section)}
              >
                查看更多
              </button>
            </div>
            <div className={styles.categoryBooks}>
              {section.novels.map((novel) => {
                const bookData = {
                  id: novel.id,
                  title: novel.title,
                  author: novel.author,
                  description: novel.description,
                  category: section.name,
                  is_completed: novel.is_completed ?? false,
                };
                return (
                  <BookCard
                    key={`${section.id}-${novel.id}`}
                    book={bookData}
                    from="/"
                    showStatus
                  />
                );
              })}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
};

export default Home;
